import React, { useState, useEffect } from 'react'
import { Button, ColorType } from '@clear/design-system'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { Text, Flex, Message, InputGroup } from '@clear/design-system'
import { ErrorMessage, Form } from '@components/layout/Form'
import useEnrollmentApi from '@hooks/useEnrollmentApi'
import { object, string, mixed } from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { promotionOpt } from '@api/enrollmentClient'
import { extractErrors } from '@utils/apiErrorMessages'
import { useSessionTimer } from '@hooks/useSessionTimer'
import { emailAddressRegex } from '@validators/schema/shared/regex'
import { invalidEmailMessage } from '@validators/schema/shared/validationTests'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { getXCLEARCorrelationInfo } from '@clear-denver/tpc-openapi-typescript/build/utils'
import { useRouter } from 'next/router'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import { setAccessToken } from '@api/utils'

interface OptInModalProps {
  setShowModal: (e: any) => void
  showModal: boolean
  email: string
  enrollmentId: string
  setIsLoading: (e: any) => void
}

function OptInModal(props: OptInModalProps) {
  const { showModal, email, enrollmentId, setIsLoading, setShowModal } = props
  const router = useRouter()
  const [contactFormEmailExists, setContactFormEmailExists] = useState(false)
  const { clearState: clearEnrollmentState } = useEnrollmentApi()
  const { isPlanBundled } = useEnrollmentContext()
  const { sessionTimeoutHandler } = useSessionTimer(clearEnrollmentState)
  const [globalError, setGlobalError] = useState<string>('')

  useEffect(() => {
    if (email && !contactFormEmailExists) {
      setContactFormEmailExists(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  // // If contact form email exists, do not validate email.
  const noValidationSchema = mixed().notRequired()
  const optInSchema = object().shape({
    ['email']: contactFormEmailExists
      ? noValidationSchema
      : string()
          .required('Email is a required field')
          .matches(emailAddressRegex, { message: invalidEmailMessage }),
  })

  const defaultFormValues = {
    email: email !== null && email !== undefined ? email : '',
  }

  const { register, handleSubmit, formState, clearErrors, reset, watch } =
    useForm<any>({
      mode: 'onChange',
      resolver: yupResolver(optInSchema),
      defaultValues: defaultFormValues,
    })

  useEffect(() => {
    reset(defaultFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, email])

  const { errors } = formState
  const watchedFields = watch()

  async function onClickSubscribe() {
    setIsLoading(true)
    clearErrors('global')
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Promotion opt in begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.PROMOTION_OPT_IN_BEGIN,
      },
    })
    try {
      await promotionOpt(
        Enrollment.ClearPromotionsOptOptEnum['In'],
        enrollmentId,
        watchedFields.email
      )
      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Promotion opt in successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.PROMOTION_OPT_IN_SUCCESSFUL,
        },
      })
      setShowModal(false)
    } catch (error) {
      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          event: DiscreteEvent.PROMOTION_OPT_IN_FAILURE,
          ...baseMetaData,
        },
      })
      if (error.status === 401) {
        sessionTimeoutHandler()
      }
      const globalErrors = await extractErrors(error, {})
      if (globalErrors.global) {
        setGlobalError(globalErrors.global[0].errorMessage)
      }
    }
    setAccessToken('')
    setIsLoading(false)
  }

  return (
    <>
      {showModal ? (
        <>
          <div
            data-cy="opt-in-modal"
            className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
            onClick={() => {
              setShowModal(false)
              setAccessToken('')
            }}
          >
            <Flex
              p={6}
              flexDirection="column"
              backgroundColor="neutralsWhite"
              width={['tablet', '695']}
              onClick={(e) => e.stopPropagation()}
              mx={[6, null]}
            >
              <Flex mb={5} ml={3}>
                <img
                  src="/clear-card-logo.svg"
                  alt="clear-logo"
                  style={{ width: '160px' }}
                />
              </Flex>
              <Flex alignItems="center" ml={7} mb={6}>
                <img
                  src="/email-logo.svg"
                  alt="email-logo"
                  style={{ width: '40px' }}
                />
                <Flex ml={3} flexDirection="column">
                  <Text fontSize="1.5rem">
                    You've Completed Your TSA PreCheck® Application
                  </Text>
                  {isPlanBundled && (
                    <Text fontSize="1rem" fontFamily="Inter-Bold" mt={1}>
                      Complete the bundle by starting your CLEAR+ enrollment
                      below.
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Flex mb={-2}>
                <Text fontSize="1rem" ml={4}>
                  {isPlanBundled
                    ? 'Save 35% on a CLEAR+ Membership if you enroll within 7 days!* Enter your email address below and use that same email address for your CLEAR+ enrollment to unlock this limited time offer.'
                    : 'Learn more about how to use CLEAR Lanes for free and save on other CLEAR travel products.*'}
                </Text>
              </Flex>
              <Form width="full">
                <InputGroup
                  label=""
                  placeholder="Email"
                  data-cy="email"
                  {...register('email')}
                  containerProps={{
                    width: [null, '100%'],
                  }}
                  error={errors.email?.message}
                />
                {globalError && <ErrorMessage>{globalError}</ErrorMessage>}
              </Form>
              <Message fontSize="12px" ml={4}>
                <span style={{ fontFamily: 'Inter-Bold' }}>
                  *You must submit your email to get the bundle discount.{' '}
                </span>
                {isPlanBundled
                  ? 'Email address must match the same email address you used to apply for TSA PreCheck through CLEAR. By submitting your email address, you are agreeing to share your email address, phone number, and zip code to receive marketing emails from CLEAR. You can opt out of marketing at any time. The CLEAR+ bundle offer only applies to non-CLEAR+ Members.'
                  : 'By submitting your email address, you are agreeing to share your email address, phone number, and zip code to receive marketing emails from CLEAR. You can opt out of marketing at any time.'}
              </Message>
              <Flex justifyContent="space-between" py={5} px={7}>
                <Button
                  size="small"
                  data-cy="no-thanks"
                  onClick={() => {
                    setShowModal(false)
                    setAccessToken('')
                  }}
                  text={isPlanBundled ? 'Skip the Bundle' : 'No Thanks'}
                  width="200px"
                  bg={'clearMidnightBlue' as ColorType}
                />
                <Button
                  size="small"
                  data-cy="subscribe"
                  disabled={errors.email?.message}
                  onClick={handleSubmit(onClickSubscribe)}
                  text={isPlanBundled ? 'Get the Bundle' : 'Subscribe'}
                  width="200px"
                  bg={'clearMidnightBlue' as ColorType}
                />
              </Flex>
            </Flex>
          </div>
          <div className="fixed inset-0 z-40 bg-black opacity-25" />
        </>
      ) : null}
    </>
  )
}

export default OptInModal
